import { motion } from "framer-motion";

const chapters = [
    {
        n: "01",
        title: "The Two-Spreadsheet Problem",
        text: "Monday: Meta CPL. Tuesday: SDR reply rates. Nobody owns the whole picture. This isn't a tool problem — it's an architecture problem.",
        image: "https://images.unsplash.com/photo-1695668548342-c0c1ad479aee",
        align: "left",
    },
    {
        n: "02",
        title: "GTM Engineering — Not Demand Gen",
        text: "Design, structure, repeatability, measurement. Clay, LeadMagic, RB2B, Instantly, Origami wired into a closed loop that identifies, enriches, sequences, and tracks — without manual handoffs.",
        image: "https://images.unsplash.com/photo-1518770660439-4636190af475",
        align: "right",
    },
    {
        n: "03",
        title: "The Dual-Engine Principle",
        text: "Payment Automation buyers don't live in one channel. Some search at 10 PM. Some scroll LinkedIn. Some are invisible until a personalized cold email lands at exactly the right moment. Catch all three.",
        image: "https://images.pexels.com/photos/9714762/pexels-photo-9714762.jpeg",
        align: "left",
    },
    {
        n: "04",
        title: "The One-Clock Rule",
        text: "Both engines move on the same timeline, reviewed in the same meeting, by the same leader. No engine gets a head start. No engine lags. They build together. They report together.",
        image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa",
        align: "right",
    },
    {
        n: "05",
        title: "The Compounding Feedback Loop",
        text: "Month 3 outperforms Month 1 by 40–60% on cost per meeting — not because you spent more. Because each engine feeds the other with intent, exclusions, and replicable creative.",
        image: "https://images.unsplash.com/photo-1508921912186-1d1a45ebb3c1",
        align: "left",
    },
    {
        n: "06",
        title: "Thirty in Thirty",
        text: "The working standard: thirty qualified meetings in thirty days from steady-state. Not leads. Meetings — with decision-makers who have budget, authority, need, and timeline.",
        image: "https://images.unsplash.com/photo-1453396450673-3fe83d2db2c4",
        align: "right",
    },
];

function Chapter({ c, i }) {
    const isLeft = c.align === "left";
    return (
        <motion.article
            data-testid={`manifesto-chapter-${c.n}`}
            initial={{ opacity: 0, y: 60 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-120px" }}
            transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
            className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-16 md:py-24 rule-top"
        >
            <div
                className={`md:col-span-5 ${
                    isLeft ? "md:order-1" : "md:order-2"
                } flex md:justify-${isLeft ? "start" : "end"}`}
            >
                <div className="relative w-full max-w-[520px] aspect-[4/5] overflow-hidden">
                    <img
                        src={c.image}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover"
                        style={{
                            filter: "grayscale(0.5) contrast(1.05) brightness(0.72)",
                        }}
                    />
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background:
                                "radial-gradient(ellipse 70% 70% at 50% 40%, transparent 30%, rgba(5,5,5,0.85) 90%)",
                        }}
                    />
                    <span className="absolute bottom-4 left-4 font-mono text-[0.68rem] uppercase tracking-[0.22em] text-white/70">
                        Fig. {String(i + 1).padStart(2, "0")}
                    </span>
                </div>
            </div>
            <div
                className={`md:col-span-7 ${
                    isLeft ? "md:order-2" : "md:order-1"
                } flex flex-col justify-center`}
            >
                <div className="flex items-baseline gap-6 mb-6">
                    <span className="number-huge text-6xl md:text-8xl">
                        {c.n}
                    </span>
                    <span className="font-mono uppercase text-[0.7rem] tracking-[0.22em] text-white/40">
                        Chapter
                    </span>
                </div>
                <h3 className="font-display uppercase text-white text-3xl md:text-5xl leading-[0.95] mb-6 balance max-w-[18ch]">
                    {c.title}
                </h3>
                <p className="font-body text-white/70 text-base md:text-lg leading-relaxed max-w-[52ch]">
                    {c.text}
                </p>
            </div>
        </motion.article>
    );
}

export default function Manifesto() {
    return (
        <section
            id="manifesto"
            data-testid="manifesto-section"
            className="container-x py-24 md:py-40"
        >
            <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9 }}
                className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12"
            >
                <div>
                    <span className="tag">Manifesto · 01–06</span>
                    <h2 className="font-display uppercase text-white text-5xl md:text-7xl mt-4 leading-[0.9] max-w-[16ch]">
                        The&nbsp;
                        <span className="font-editorial italic font-normal normal-case text-[#e84125] tracking-normal">
                            architecture&nbsp;
                        </span>
                        underneath the tactics.
                    </h2>
                </div>
                <p className="font-body text-white/60 max-w-[42ch] md:text-right">
                    Six ideas that reframe how Payment Automation SaaS leaders
                    build pipeline. Pulled straight from the book.
                </p>
            </motion.div>
            <div>
                {chapters.map((c, i) => (
                    <Chapter key={c.n} c={c} i={i} />
                ))}
            </div>
        </section>
    );
}
